import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { toast } from "sonner";

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function fmt(d) {
  return d.toLocaleDateString("en-IN", { day: "2-digit" });
}

const STATUS_COLOR = {
  checked_in: "bg-emerald-500/70",
  confirmed: "bg-[#D4AF37]/80",
  pending: "bg-orange-500/70",
  checked_out: "bg-white/20",
};

export default function CalendarView() {
  const [cursor, setCursor] = useState(new Date());
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { room, date }

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const numDays = daysInMonth(year, month);
  const dates = useMemo(() => Array.from({ length: numDays }, (_, i) => new Date(year, month, i + 1)), [year, month, numDays]);
  const today = new Date();

  async function load() {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/rooms"),
        api.get("/reservations", { params: { limit: 300 } }),
      ]);
      setRooms(r1.data);
      setReservations(r2.data);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  // Build cell -> reservation map
  const cellMap = useMemo(() => {
    const m = new Map();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    for (const r of reservations) {
      const ci = new Date(r.check_in);
      const co = new Date(r.check_out);
      if (co < monthStart || ci > monthEnd) continue;
      const start = ci < monthStart ? monthStart : ci;
      const end = co > monthEnd ? new Date(monthEnd.getTime() + 86400000) : co;
      const cur = new Date(start);
      while (cur < end) {
        const key = `${r.room_id}:${cur.getDate()}`;
        m.set(key, r);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return m;
  }, [reservations, year, month]);

  function shiftMonth(delta) {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  }

  function gotoToday() {
    setCursor(new Date());
  }

  function onCellClick(room, date) {
    const key = `${room.id}:${date.getDate()}`;
    if (cellMap.has(key)) return; // occupied
    if (date < new Date(today.getFullYear(), today.getMonth(), today.getDate())) return;
    setDialog({ room, date });
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Calendar</div>
          <h1 className="font-display text-3xl md:text-4xl">{cursor.toLocaleString("en-IN", { month: "long", year: "numeric" })}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftMonth(-1)} className="btn-ghost-gold rounded-md p-2" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={gotoToday} className="btn-ghost-gold rounded-md px-3 py-2 text-sm" data-testid="cal-today">Today</button>
          <button onClick={() => shiftMonth(1)} className="btn-ghost-gold rounded-md p-2" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl overflow-hidden" data-testid="calendar-grid">
        <div className="overflow-x-auto">
          <div style={{ minWidth: 80 + numDays * 34 }}>
            {/* Header row */}
            <div className="flex sticky top-0 bg-[#0a0f1c] border-b border-white/5 z-10">
              <div className="w-20 shrink-0 px-3 py-3 text-xs uppercase tracking-widest text-white/50 border-r border-white/5">Room</div>
              {dates.map((d) => {
                const isToday = sameDay(d, today);
                return (
                  <div key={d.getDate()} className={`w-[34px] py-2 text-[11px] text-center border-r border-white/5 ${isToday ? "bg-[#D4AF37]/10 text-[#D4AF37] font-semibold" : "text-white/50"}`}>
                    <div>{d.toLocaleDateString("en-IN", { weekday: "narrow" })}</div>
                    <div className="mt-0.5">{fmt(d)}</div>
                  </div>
                );
              })}
            </div>
            {loading && <div className="p-8 text-white/50 text-sm">Loading calendar…</div>}
            {!loading && rooms.map((room) => (
              <div key={room.id} className="flex border-b border-white/5 hover:bg-[#1A2235]/40" data-testid={`cal-room-${room.room_number}`}>
                <div className="w-20 shrink-0 px-3 py-2 border-r border-white/5">
                  <div className="text-sm font-medium">{room.room_number}</div>
                  <div className="text-[10px] text-white/40 truncate">{room.room_type}</div>
                </div>
                {dates.map((d) => {
                  const key = `${room.id}:${d.getDate()}`;
                  const res = cellMap.get(key);
                  const past = d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  return (
                    <button
                      key={d.getDate()}
                      onClick={() => onCellClick(room, d)}
                      className={`w-[34px] h-10 border-r border-white/5 relative ${past ? "opacity-50" : "hover:bg-[#D4AF37]/5"} transition`}
                      title={res ? `${res.guest_name} (${res.status})` : `${room.room_type} · ${d.toLocaleDateString("en-IN")}`}
                    >
                      {res && (
                        <span className={`absolute inset-1 rounded ${STATUS_COLOR[res.status] || "bg-white/20"}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
            {!loading && rooms.length === 0 && <div className="p-8 text-white/50 text-sm">No rooms yet.</div>}
          </div>
        </div>
      </div>

      <div className="flex gap-4 text-xs text-white/50">
        {Object.entries(STATUS_COLOR).map(([k, c]) => (
          <div key={k} className="flex items-center gap-2"><span className={`w-3 h-3 rounded ${c}`} />{k.replace("_", " ")}</div>
        ))}
      </div>

      {dialog && <QuickBookDialog ctx={dialog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function QuickBookDialog({ ctx, onClose, onDone }) {
  const [guest, setGuest] = useState("");
  const [nights, setNights] = useState(1);
  const [rate, setRate] = useState(ctx.room.base_rate);
  const [channel, setChannel] = useState("Direct");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const ci = new Date(ctx.date); ci.setHours(12, 0, 0, 0);
      const co = new Date(ci); co.setDate(co.getDate() + Number(nights));
      await api.post("/reservations", {
        room_id: ctx.room.id,
        guest_name: guest,
        guests_count: 2,
        check_in: ci.toISOString(),
        check_out: co.toISOString(),
        rate_per_night: Number(rate),
        channel,
      });
      toast.success(`Reservation created for ${ctx.room.room_number}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose} data-testid="quick-book-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#12182B] border border-white/10 rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D4AF37]">Quick booking</div>
            <div className="font-display text-xl mt-1">Room {ctx.room.room_number} · {ctx.room.room_type}</div>
            <div className="text-xs text-white/50 mt-1">Starting {ctx.date.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Guest name</label>
            <input required value={guest} onChange={(e) => setGuest(e.target.value)} className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/60" data-testid="qb-guest" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Nights</label>
              <input type="number" min={1} value={nights} onChange={(e) => setNights(e.target.value)} className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2.5 text-sm" data-testid="qb-nights" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Rate (₹)</label>
              <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2.5 text-sm" data-testid="qb-rate" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Channel</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2.5 text-sm" data-testid="qb-channel">
              {["Direct", "Booking.com", "MakeMyTrip", "Agoda", "Expedia", "Goibibo"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={loading} className="btn-gold w-full rounded-md py-3 mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="qb-submit">
          <Plus className="w-4 h-4" /> {loading ? "Creating…" : "Create reservation"}
        </button>
      </form>
    </div>
  );
}
